<template>
  <div>
    <div class="rounded bg-white p-5">
      <XHeader />

      <div>
        <span class="block mb-5 sm:px-10 text-lg text-grey-30 font-medium text-center">
          {{ $t('common.confirmInvitation.sections.basicInfo.title') }}
        </span>

        <div>
          <div class="flex flex-wrap -mx-3">
            <div class="w-full sm:w-1/2 px-3 mb-3">
              <XField :errors="validationErrors('name')">
                <XInput
                  :label="$t('common.formFields.firstName')"
                  color="secondary"
                  v-model.trim="fields.name"
                  data-vv-name="name"
                  v-validate="'required|min:3|max:50'"
                  :invalid="validationHasErrors('name')"
                  @blur="validateField('name')" />
              </XField>
            </div>
            <div class="w-full sm:w-1/2 px-3 mb-3">
              <XField>
                <XInput
                  :label="$t('common.formFields.lastName')"
                  color="secondary"
                  v-model.trim="fields.lastName"
                  optional />
              </XField>
            </div>
          </div>
          <div class="flex flex-wrap -mx-3">
            <div class="w-full sm:w-1/2 px-3 mb-3">
              <XField>
                <XInput
                  :label="$t('common.formFields.email')"
                  color="secondary"
                  :value="email"
                  disabled />
              </XField>
            </div>
            <div class="w-full sm:w-1/2 px-3 mb-3">
              <XField :errors="validationErrors('company')">
                <XInput
                  :label="$t('common.formFields.company')"
                  color="secondary"
                  v-model.trim="fields.company"
                  data-vv-name="company"
                  v-validate="'required|min:3|max:50'"
                  :invalid="validationHasErrors('company')"
                  @blur="validateField('company')" />
              </XField>
            </div>
          </div>

          <div>
            <XField
              class="mb-3"
              :errors="validationErrors('password')">
              <div class="relative">
                <XInput
                  :label="$t('common.formFields.password')"
                  :type="passwords.main.type"
                  color="secondary"
                  v-model.trim="fields.password"
                  data-vv-name="password"
                  v-validate="'required|password:8'"
                  :invalid="validationHasErrors('passwordConfirmation')"
                  @blur="validateField('password')" />
                <XIcon
                  slot="extra"
                  :name="passwords.main.icon"
                  class="absolute pin-r mr-4 mt-2 text-brand-default"
                  :class="$style['show-password-icon']"
                  @click="changePasswordType('main')" />
              </div>
            </XField>
          </div>
          <div>
            <XField
              class="mb-3"
              :errors="validationErrors('passwordConfirmation')">
              <div class="relative">
                <XInput
                  :label="$t('common.formFields.passwordConfirmation')"
                  :type="passwords.confirmation.type"
                  color="secondary"
                  v-model.trim="passwordConfirmation"
                  data-vv-name="passwordConfirmation"
                  v-validate="'required|is:' + fields.password"
                  :invalid="validationHasErrors('passwordConfirmation')"
                  @blur="validateField('passwordConfirmation')" />
                <XIcon
                  slot="extra"
                  :name="passwords.confirmation.icon"
                  class="absolute pin-r mr-4 mt-2 text-grey-40"
                  :class="$style['show-password-icon']"
                  @click="changePasswordType('confirmation')" />
              </div>
            </XField>
          </div>
          <div>
            <XField
              class="mb-3"
              :errors="validationErrors('terms')">
              <XCheckbox
                id="sign-up-form-terms"
                color="secondary"
                v-model="fields.termsConfirmed"
                data-vv-name="terms"
                v-validate="'required:true'"
                @blur="validateField('terms')">
                <i18n
                  slot="label"
                  path="common.confirmInvitation.terms.base"
                  tag="span">
                  <a
                    place="platform"
                    href="https://www.yumiwi.com/es/terminos-y-condiciones/"
                    target="_blank"
                    class="text-info-50">
                    {{ $t('common.confirmInvitation.terms.platform') }}
                  </a>
                </i18n>
              </XCheckbox>
            </XField>

            <XField
              class="mb-3"
              :errors="validationErrors('privacy')">
              <XCheckbox
                id="sign-up-form-privacy"
                color="secondary"
                v-model="fields.privacyConfirmed"
                data-vv-name="privacy"
                v-validate="'required:true'"
                @blur="validateField('privacy')">
                <i18n
                  slot="label"
                  path="common.confirmInvitation.privacy.base"
                  tag="span">
                  <a
                    place="privacy"
                    href="https://www.yumiwi.com/es/politica-de-privacidad/"
                    target="_blank"
                    class="text-info-50">
                    {{ $t('common.confirmInvitation.privacy.privacy') }}
                  </a>
                </i18n>
              </XCheckbox>
            </XField>

            <XField
              class="mb-6"
              :errors="validationErrors('communicationsConfirmed')">
              <XCheckbox
                color="secondary"
                value="1"
                :label="$t('common.formFields.communications')"
                v-model="fields.communicationsConfirmed"
                data-vv-name="communications"
                optional />
            </XField>
          </div>
        </div>

        <div class="text-right">
          <XButton
            color="secondary"
            :loading="loading"
            @click="next">
            {{ isAdmin ? $t('common.confirmInvitation.actions.next') : $t('common.confirmInvitation.actions.signUp') }}
          </XButton>
        </div>
      </div>
    </div>

    <div class="mt-2 pb-2">
      <p class="text-xs text-brand-default-darkest text-justify">
        {{ $t('common.confirmInvitation.privacyLegend.terms.info') }}
        <strong class="font-bold">{{ $t('common.confirmInvitation.privacyLegend.terms.purposes.title') }}</strong> {{ $t('common.confirmInvitation.privacyLegend.terms.purposes.text') }}
        <strong class="font-bold">{{ $t('common.confirmInvitation.privacyLegend.terms.legitimation.title') }}</strong> {{ $t('common.confirmInvitation.privacyLegend.terms.legitimation.text') }}
        <strong class="font-bold">{{ $t('common.confirmInvitation.privacyLegend.terms.recipients.title') }}</strong> {{ $t('common.confirmInvitation.privacyLegend.terms.recipients.text') }}
        <strong class="font-bold">{{ $t('common.confirmInvitation.privacyLegend.terms.rights.title') }}</strong>
        <i18n
          path="common.confirmInvitation.privacyLegend.terms.rights.text"
          tag="span">
          <a
            place="action"
            href="mailto:info@yumiwi.com"
            class="text-grey-30">info@yumiwi.com</a>
        </i18n>
      </p>
    </div>
  </div>
</template>

<script>
import FormValidation from '@common/mixins/FormValidation'

import XHeader from './common/Header'

export default {
  mixins: [FormValidation],

  components: {
    XHeader
  },

  props: {
    value: {
      type: Object
    },
    email: {
      type: String
    },
    isAdmin: {
      type: Boolean
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      fields: this.value,
      passwordConfirmation: '',
      passwords: {
        main: {
          icon: 'fa/solid/eye',
          type: 'password'
        },
        confirmation: {
          icon: 'fa/solid/eye',
          type: 'password'
        }
      }
    }
  },

  methods: {
    next () {
      this.validate()
        .then(result => {
          if (!result) {
            return
          }

          if (this.isAdmin) {
            this.$emit('next')
          } else {
            this.$emit('submit')
          }
        })
        .catch(error => {
          let errors = error.networkError
            ? error.networkError
            : error.graphQLErrors

          this.$emit('errors', errors)
        })
    },

    changePasswordType (password) {
      if (this.passwords[password].type === 'password') {
        this.passwords[password].icon = 'fa/solid/eye-slash'
        this.passwords[password].type = 'text'
      } else {
        this.passwords[password].icon = 'fa/solid/eye'
        this.passwords[password].type = 'password'
      }
    }
  }
}
</script>

<style lang="scss" module>
.show-password-icon {
  top: calc(50% + 5px);
  transform: translateY(-50%);
}
</style>
